<template>
  <v-dialog v-model="visible" persistent width="1200" scrollable>
    <v-card :loading="loading">
      <v-card-title>
        <span class="headline">{{ $t("modal.updateCase.title") }}</span>
      </v-card-title>
      <v-card-text>
        <!-- Update case form -->
        <v-form ref="updateCaseForm">
          <v-container fluid>
            <v-row>
              <v-col cols="2">
                <v-text-field dense :maxlength="20" :label="$t('modal.updateCase.label.localId')" v-model="editCase.localId" :hint="$t('hint.localId')" persistent-hint />
              </v-col>
              <v-col cols="2">
                <v-text-field dense :maxlength="20" :label="$t('label.title')" v-model="editCase.title" />
              </v-col>
              <v-spacer />
              <v-col cols="7">
                <v-text-field dense :maxlength="100" :label="$t('label.name')" v-model="editCase.name" :rules="[rules.required]" />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="4">
                <v-select dense :items="types" v-model="editCase.type" :label="$t('label.type')" />
              </v-col>
              <v-spacer />
              <v-col cols="3">
                <v-text-field dense :maxlength="100" :label="$t('modal.updateCase.label.placeOfBirth')" v-model="editCase.placeOfBirth" />
              </v-col>
              <v-col cols="4">
                <v-menu v-model="dobPicker" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field dense clearable v-model="editCase.dob" label="Date of Birth" prepend-icon="mdi-calendar" readonly v-bind="attrs" v-on="on"></v-text-field>
                  </template>
                  <v-date-picker no-title :show-current="false" v-model="editCase.dob" @input="dobPicker = false"></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="2">
                <v-text-field dense :maxlength="100" :label="$t('modal.updateCase.label.citizenship')" v-model="editCase.citizenship" />
              </v-col>
              <v-col cols="2">
                <v-text-field dense :maxlength="100" :label="$t('modal.updateCase.label.phoneNumber')" v-model="editCase.phoneNumber" />
              </v-col>
              <v-col cols="2">
                <v-select dense clearable :items="genders" v-model="editCase.gender" :label="$t('modal.updateCase.label.gender')" />
              </v-col>
            </v-row>

            <!-- Client custom labels -->
            <v-row v-if="getAccountSettings.customLabels">
              <v-col cols="3" v-for="(value, labelName) in getAccountSettings.customLabels" :key="labelName">
                <v-select 
                  :items=value 
                  :label=labelName
                   v-model="editCase.customLabels[labelName]" 
                   clearable 
                   dense>
                </v-select>
              </v-col>
            </v-row>

            <!-- Client custom fields -->
            <v-row v-if="getAccountSettings.customFields">
              <v-col cols="3"
                v-for="(fieldName) in getAccountSettings.customFields"
                :key="fieldName">
                  <v-text-field
                    dense
                    :label="fieldName"
                    v-model="editCase.customFields[fieldName]"
                  />
              </v-col>
            </v-row>



            <v-row no-gutters align="center">
              <v-col cols="2">
                <h3>{{ $t("label.address") }}</h3>
              </v-col>
              <v-col>
                <v-divider />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="6">
                <v-text-field dense :maxlength="100" :label="$t('modal.updateCase.label.address1')" v-model="editCase.address.address1" />
              </v-col>
              <v-col cols="2">
                <v-text-field dense :maxlength="100" :label="$t('modal.updateCase.label.address2')" v-model="editCase.address.address2" />
              </v-col>
              <v-col cols="2">
                <v-text-field dense :maxlength="100" :label="$t('modal.updateCase.label.address3')" v-model="editCase.address.address3" />
              </v-col>
              <v-col cols="2">
                <v-text-field dense :maxlength="100" :label="$t('modal.updateCase.label.address4')" v-model="editCase.address.address4" />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="3">
                <v-text-field dense :maxlength="100" :label="$t('modal.updateCase.label.city')" v-model="editCase.address.city" />
              </v-col>
              <v-col cols="3">
                <v-text-field dense :maxlength="100" :label="$t('modal.updateCase.label.stateOrProvince')" v-model="editCase.address.stateOrProvince" />
              </v-col>
              <v-col cols="3">
                <v-text-field dense :maxlength="100" :label="$t('modal.updateCase.label.postalCode')" v-model="editCase.address.postalCode" />
              </v-col>
              <v-col cols="3">
                <v-text-field dense :maxlength="100" :label="$t('modal.updateCase.label.country')" v-model="editCase.address.country" />
              </v-col>
            </v-row>

            <!-- Tax IDs -->
            <v-row>
              <v-col cols="6">
                <v-row align="center">
                  <v-col>
                    <h3>Tax IDs</h3>
                  </v-col>
                  <v-col cols="1">
                    <v-btn color="green lighten-1" icon @click="addId">
                      <v-icon dark>mdi-plus</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>

                <v-row v-if="!editCase.identification || editCase.identification.length == 0" dense>
                  <v-alert dense outlined type="info">Click <v-icon small color="success">mdi-plus</v-icon> to add tax IDs.</v-alert>
                </v-row>

                <v-row v-for="(caseId, index) in caseIds" :key="index">

                  <v-col cols="3" v-if="caseId.newId">
                    <v-select dense :items="idTypes" v-model="caseId.type" :label="$t('label.type')" />
                  </v-col>
                  <v-col cols="3" v-else>
                    <div class="overline font-weight-black">Type</div>
                    {{ caseId.type}}
                  </v-col>

                  <v-col cols="4" v-if="caseId.newId">
                    <v-text-field dense label="Country" hide-details :maxlength="100" v-model="caseId.country" />
                  </v-col>
                  <v-col cols="4" v-else>
                    <div class="overline font-weight-black">Country</div>
                    {{ caseId.country}}
                  </v-col>

                  <v-col cols="4" v-if="caseId.newId">
                    <v-text-field dense label="ID Number" hide-details :maxlength="100" v-model="caseId.idNumber" />
                  </v-col>
                  <v-col cols="4" v-else>
                    <div class="overline font-weight-black">ID Number</div>
                    {{ caseId.idNumber}}
                  </v-col>

                  <v-col cols="1">
                    <v-btn color="red lighten-1" dark icon @click="deleteId(index)">
                      <v-icon dark>mdi-close</v-icon>
                    </v-btn>
                  </v-col>

                </v-row>

              </v-col>

              <!-- cryptopWallet -->
              <v-col>
                <v-row dense>
                  <v-col>
                    <h3>Crypto Wallet</h3>
                    <v-row nogutters>
                      <v-col cols="4">
                        <v-text-field :maxlength="100"
                          label="Name"
                          v-model="editCase.cryptoWallet.walletName" />
                      </v-col>
                      <v-col cols="8">
                        <v-text-field :maxlength="100"
                          label="Wallet ID"
                          v-model="editCase.cryptoWallet.walletId" />
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-col>

            </v-row>

            <v-row align="center" class="pt-4">
              <v-col cols="2">
                <h3>Other IDs</h3>
              </v-col>
              <v-col>
                <!-- <v-divider /> -->
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="6">
                <v-text-field dense :maxlength="100" :label="$t('modal.updateCase.label.passportCountry')" v-model="editCase.passport.passportCountry" />
              </v-col>
              <v-col cols="6">
                <v-text-field dense :maxlength="100" :label="$t('modal.updateCase.label.passportNumber')" v-model="editCase.passport.passportNumber" />
              </v-col>

              <v-col cols="6">
                <v-text-field dense :maxlength="100" :label="$t('modal.updateCase.label.licenseCountry')" v-model="editCase.driverLicense.licenseCountry" />
              </v-col>
              <v-col cols="6">
                <v-text-field dense :maxlength="100" :label="$t('modal.updateCase.label.licenseNumber')" v-model="editCase.driverLicense.licenseNumber" />
              </v-col>
            </v-row>

            <!-- compliance and attributes -->
            <v-row dense>
              <v-col cols="5">
                <v-row align="center">
                  <v-col cols="4">
                    <h3>{{ $t("label.compliance") }}</h3>
                  </v-col>
                  <v-col>
                    <v-divider />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12">
                    <v-autocomplete v-model="editCase.caseGroupId" :items="caseGroups" :label="$t('modal.addCase.label.caseGroups')" :hint="$t('hint.caseGroups')" clearable persistent-hint item-text="name" item-value="id" dense />
                  </v-col>
                </v-row>
              </v-col>

              <v-spacer />

              <v-col cols="6">
                <!-- attributes -->
                <v-row align="center" dense>
                  <v-col cols="4">
                    <h3>
                      {{ $t("modal.addCase.label.customAttributes") }}
                    </h3>
                  </v-col>
                  <v-col>
                    <v-divider />
                  </v-col>
                  <v-col cols="1">
                    <v-btn color="green lighten-1" icon @click="addAttribute">
                      <v-icon dark>mdi-plus</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>

                <v-row dense v-for="(attr, index) in customAttributes" :key="index">
                  <v-col>
                    <v-text-field outlined dense hide-details :maxlength="100" v-model="attr.key" />
                  </v-col>
                  <v-col>
                    <v-text-field outlined dense hide-details :maxlength="100" v-model="attr.value" />
                  </v-col>
                  <v-col cols="1">
                    <v-btn color="red lighten-1" dark icon @click="deleteAttribute(index)">
                      <v-icon dark>mdi-close</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn color="blue darken-1" text @click="onCancel">{{
          $t("label.cancel")
          }}</v-btn>
        <v-btn dark color="blue darken-1" @click="onSave" :loading="loading">{{
          $t("label.save")
          }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import {getMinDateString} from "../../utils/dateUtils"
const model = { key: "", value: "" };
const idStruct = {type: "", country: "", idNumber: "", newId: true};
export default {
  name: "EditCase",
  props: {
    selectedCase: {},
    caseGroups: {},
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      editCase: {
        ...this.selectedCase,
        type: this.selectedCase.type ? this.selectedCase.type.toLowerCase(): undefined,
        address: this.selectedCase.address ? this.selectedCase.address : {},
        passport: this.selectedCase.passport ? this.selectedCase.passport : {},
        customLabels: this.selectedCase.customLabels ? this.selectedCase.customLabels : {},
        customFields: this.selectedCase.customFields ? this.selectedCase.customFields : {},
        cryptoWallet: this.selectedCase.cryptoWallet ? this.selectedCase.cryptoWallet : {},
        driverLicense: this.selectedCase.driverLicense
          ? this.selectedCase.driverLicense
          : {},
      },
      customAttributes: [{ key: null, value: null }], // null values avoids saving empty attributes, ex: {"":""}
      caseIds:[],
      loading: false,
      dobPicker: false,
      rules: {
        required: (value) => !!value || "Required.",
      },
      types: [
      { text: 'Individual', value: 'individual'},
      { text: 'Entity', value: 'entity'},
      { text: 'Vessel', value: 'vessel'},
      ],
      idTypes: ["VAT", "TIN"],
      genders: ["Male", "Female"],
    };
  },
  computed: {
    ...mapGetters(["getProfile", "getAccountSettings"]),
  },
  created() {
    // If this case has attributes, clear default attribute and populate with existing attributes
    if (
      this.selectedCase.caseAttributes &&
      Object.keys(this.selectedCase.caseAttributes).length > 0
    ) {
      this.customAttributes = [];
      Object.entries(this.selectedCase.caseAttributes).map(([key, value]) => {
        this.customAttributes.push({ key: key, value: value });
      });
    }

    if(this.selectedCase.identification && this.selectedCase.identification.length > 0){
      this.caseIds = [...this.selectedCase.identification]
    }
  },
  methods: {
    ...mapActions(["updateCase", "setGlobalAlert"]),
    getMinDateString,
    addAttribute() {
      this.customAttributes.push({ ...model });
    },
    deleteAttribute(index) {
      this.customAttributes.splice(index, 1);
    },
    addId() {
      this.caseIds.push({ ...idStruct });
    },
    deleteId(index){
      this.caseIds.splice(index, 1);
    },
    //submit - TODO catch error if case group was deleted prior to saving case
    async onSave() {
      if (!this.$refs.updateCaseForm.validate()) {
        return false;
      }

      this.loading = true;

      let caseRecordRequest = JSON.parse(JSON.stringify(this.editCase))


      if(!caseRecordRequest.driverLicense.licenseNumber){
        caseRecordRequest.driverLicense = null
      }

      // set caseAttributes
      // TODO need to block blank attributes - maybe should do this on the back end
      caseRecordRequest.caseAttributes = Object.fromEntries(
        this.customAttributes.map(({ key, value }) => [key, value]) // iterate customAttributes, ex: {key:"theKey", value:"theValue"} => ["theKey":"theValue"]
      );

      caseRecordRequest.identification = this.caseIds;

      try {
        await this.updateCase(caseRecordRequest).then(() => {
          this.$emit("onConfirmed");
          this.setGlobalAlert({
            alert: true,
            color: "green",
            alertMessage: "Case Updated",
          });
        });
      } catch (e) {
        this.setGlobalAlert({
          alert: true,
          color: "red",
          alertMessage: this.$t("error.general.unhandled"),
        });
      }

      this.loading = false;
    },
    onCancel() {
      this.$emit("onCancelled");
    },
  },
};
</script>